import { EmailListSend, OptionsApi } from '@core/swagger-client';
import { Dialog } from '@core/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewPanelOrganism } from '../../organism';

interface Props {
    listId: string;
    open: boolean;
    send: EmailListSend | undefined;
    onClose: () => void;
}

/**
 * Read-only view of the exact email a historic send used: fetches the (raw) snapshot template and
 * renders it to HTML via the same preview path as the editor. Gated by CanManageEmailLists only.
 */
export const ViewSendTemplateDialog = ({ listId, open, send, onClose }: Props) => {
    const { t } = useTranslation();

    const { data: template, isLoading } = useQuery(
        OptionsApi.emailLists.getSendTemplate(listId, send?.sendId ?? '', { enabled: open && !!send })
    );
    const previewMutation = useMutation(OptionsApi.emailLists.previewTemplate());

    // The dialog now stays mounted across opens, so clear the previous send's rendered preview
    // whenever switching to a new one rather than briefly showing stale content.
    useEffect(() => {
        previewMutation.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [send?.sendId]);

    // Render the raw markdown snapshot to HTML once it loads.
    useEffect(() => {
        if (!template || !send) return;
        previewMutation.mutate({ templateType: send.templateType, subject: template.subject, body: template.body });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template]);

    const rendered = previewMutation.data;

    return (
        <Dialog open={open} onClose={onClose} title={t('EmailLists.View.Title')} widthClassName="max-w-3xl">
            <PreviewPanelOrganism
                isLoading={isLoading || previewMutation.isPending}
                subject={rendered?.subject}
                html={rendered?.body ?? ''}
            />
        </Dialog>
    );
};
